import React, { useState, useEffect } from "react";
import {
  BookOpen,
  Users,
  Target,
  TrendingUp,
  Calendar,
  CheckCircle,
  ArrowRight,
  Download,
  Mail,
  Phone,
  Star,
  Shield,
  Gift,
  ChevronRight,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Type definitions
interface Product {
  name: string;
  price: string;
  sales: string;
  revenue: string;
}

interface Strategy {
  difficulty: string;
  timeline: string;
  focus: string;
  color: string;
}

interface UserData {
  name: string;
  email: string;
  knowledge: string;
  experience: string;
  audience: string;
  timeCommitment: string;
  currentIncome: string;
  goal: string;
  potentialRevenue: number;
  monthlyGoal: number;
  timeToGoal: number;
  strategy: Strategy;
  recommendedProducts: Product[];
}

const MonetizationStrategy: React.FC = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [emailParam, setEmailParam] = useState<string>("");

  useEffect(() => {
    const fetchUserData = async (email: string): Promise<void> => {
      try {
        setLoading(true);

        // Simulate API call - replace with actual backend call
        const response = await fetch(
          `/api/user-strategy?email=${encodeURIComponent(email)}`,
        );

        if (!response.ok) {
          throw new Error("User data not found");
        }

        const data: UserData = await response.json();
        setUserData(data);
      } catch (err) {
        // Fallback: Create sample data based on email for demo
        const sampleData = generateSampleData(email);
        setUserData(sampleData);
      } finally {
        setLoading(false);
      }
    };
    // Get email from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    setEmailParam(email || "");

    if (email) {
      fetchUserData(email);
    } else {
      setError("No email parameter found in URL");
      setLoading(false);
    }
  }, []);
  console.log(emailParam);

  // Generate sample data for demo purposes
  const generateSampleData = (email: string): UserData => {
    // const domains = ["gmail", "yahoo", "outlook"];
    // const isGmail = email?.includes("gmail");

    return {
      name:
        email?.split("@")[0]?.replace(/[0-9]/g, "")?.toLowerCase() || "User",
      email: email,
      knowledge: "Digital Marketing & Social Media",
      experience: "Intermediate (2-3 years)",
      audience: "Growing audience (1K-10K)",
      timeCommitment: "16-30 hours (Serious commitment)",
      currentIncome: "₦51K - ₦200K",
      goal: "₦1M - ₦3M",
      potentialRevenue: 850000,
      monthlyGoal: 2000000,
      timeToGoal: 8,
      strategy: {
        difficulty: "Challenging but realistic",
        timeline: "6-12 months",
        focus: "Scale existing products and add premium offerings",
        color: "text-yellow-600",
      },
      recommendedProducts: [
        {
          name: "Ebook/Mini-Guide",
          price: "₦5K-15K",
          sales: "30-80/month",
          revenue: "₦150K-1.2M",
        },
        {
          name: "Video Course",
          price: "₦25K-80K",
          sales: "8-20/month",
          revenue: "₦200K-1.6M",
        },
        {
          name: "Coaching Program",
          price: "₦100K-300K",
          sales: "3-7/month",
          revenue: "₦300K-2.1M",
        },
        {
          name: "Templates/Tools",
          price: "₦10K-25K",
          sales: "15-30/month",
          revenue: "₦150K-750K",
        },
      ],
    };
  };

  const formatCurrency = (amount: number): string => {
    return `₦${amount?.toLocaleString()}`;
  };

  const capitalizeWords = (str: string): string => {
    return (
      str
        ?.split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ") || ""
    );
  };

  const handleQuizRedirect = (): void => {
    navigate("/skill-to-sales/quiz");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your personalized strategy...</p>
        </div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Strategy Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            We couldn't find your monetization strategy. This might happen if:
          </p>
          <ul className="text-left text-gray-600 mb-6 space-y-2">
            <li>• The link has expired</li>
            <li>• You haven't completed the quiz yet</li>
            <li>• There was an error with your submission</li>
          </ul>
          <button
            onClick={handleQuizRedirect}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Take the Quiz Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">
              Your Monetization Strategy
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Hello, {capitalizeWords(userData.name)}!
              </span>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">
                Current Potential
              </h3>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(userData.potentialRevenue)}
            </p>
            <p className="text-xs text-gray-500 mt-1">per month</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Your Goal</h3>
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(userData.monthlyGoal)}
            </p>
            <p className="text-xs text-gray-500 mt-1">target income</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Timeline</h3>
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {userData.timeToGoal}
            </p>
            <p className="text-xs text-gray-500 mt-1">months to goal</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Difficulty</h3>
              <Zap className="w-5 h-5 text-yellow-600" />
            </div>
            <p className={`text-lg font-bold ${userData.strategy.color}`}>
              {userData.strategy.difficulty.split(" ")[0]}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {userData.strategy.timeline}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Strategy */}
          <div className="lg:col-span-2 space-y-8">
            {/* Your Profile */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <Users className="w-6 h-6 mr-3 text-blue-600" />
                Your Knowledge Profile
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Expertise Area
                  </h4>
                  <p className="text-gray-600 mb-4">{userData.knowledge}</p>

                  <h4 className="font-semibold text-gray-800 mb-2">
                    Experience Level
                  </h4>
                  <p className="text-gray-600 mb-4">{userData.experience}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Current Audience
                  </h4>
                  <p className="text-gray-600 mb-4">{userData.audience}</p>

                  <h4 className="font-semibold text-gray-800 mb-2">
                    Time Commitment
                  </h4>
                  <p className="text-gray-600">{userData.timeCommitment}</p>
                </div>
              </div>
            </div>

            {/* Recommended Products */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <BookOpen className="w-6 h-6 mr-3 text-blue-600" />
                Your Product Roadmap
              </h2>
              <div className="space-y-4">
                {userData.recommendedProducts.map(
                  (product: Product, index: number) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {product.name}
                        </h3>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          Phase {index + 1}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Pricing</p>
                          <p className="font-semibold text-gray-800">
                            {product.price}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Expected Sales</p>
                          <p className="font-semibold text-gray-800">
                            {product.sales}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Revenue Potential</p>
                          <p className="font-semibold text-green-600">
                            {product.revenue}
                          </p>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* 90-Day Action Plan */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <Calendar className="w-6 h-6 mr-3 text-blue-600" />
                Your 90-Day Action Plan
              </h2>
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Days 1-30: Foundation
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Create your first digital product (ebook/mini-course)
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Set up email marketing system
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Launch on social media with 5 posts per week
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Target: First 10 sales and 500 email subscribers
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-yellow-500 pl-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Days 31-60: Scale
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Launch video course based on initial feedback
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Start affiliate recruitment program
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Create bundle offers and upsells
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Target: ₦300K+ monthly revenue
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 pl-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Days 61-90: Premium
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Launch coaching/consulting services
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Host first live workshop/webinar
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Develop done-for-you products
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Target: ₦800K+ monthly revenue
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Book Offer */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-xl shadow-lg p-8 text-white">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">
                  The W.E.A.L.T.H Method
                </h3>
                <p className="text-blue-100 text-sm">
                  Complete 86,000-word guide to digital money in Nigeria
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center">
                  <Star className="w-5 h-5 mr-3 text-yellow-400" />
                  <span className="text-sm">
                    Comprehensive strategies for every knowledge area
                  </span>
                </div>
                <div className="flex items-center">
                  <Shield className="w-5 h-5 mr-3 text-green-400" />
                  <span className="text-sm">
                    Proven methods used by top Nigerian earners
                  </span>
                </div>
                <div className="flex items-center">
                  <Gift className="w-5 h-5 mr-3 text-pink-400" />
                  <span className="text-sm">
                    Bonus templates and worksheets included
                  </span>
                </div>
              </div>

              <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm">Regular Price:</span>
                  <span className="line-through text-blue-200">₦50,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Your Price:</span>
                  <span className="text-2xl font-bold">₦25,000</span>
                </div>
              </div>

              <button className="w-full bg-white text-blue-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center">
                Get Your Copy Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Strategy Summary
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Revenue Gap:</span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(
                      userData.monthlyGoal - userData.potentialRevenue,
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Growth Needed:</span>
                  <span className="font-semibold text-blue-600">
                    {Math.round(
                      (userData.monthlyGoal / userData.potentialRevenue - 1) *
                        100,
                    )}
                    %
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Focus Area:</span>
                  <span className="font-semibold text-gray-800 text-sm">
                    {userData.strategy.focus.split(" ").slice(0, 3).join(" ")}
                    ...
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Support */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Need Help?
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Have questions about your strategy? Our team is here to help.
              </p>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Support
                </button>
                <button className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <Phone className="w-4 h-4 mr-2" />
                  Schedule Call
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 text-white mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">
              Ready to Turn Your Knowledge Into Wealth?
            </h3>
            <p className="text-gray-300 mb-6">
              Your personalized strategy is just the beginning. Get the complete
              W.E.A.L.T.H Method today.
            </p>
            <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center mx-auto">
              Get The Complete Guide
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonetizationStrategy;
